/**
 * The chat session: everything the trainer's conversations DO between the screen and the wire, in
 * one module with no React, no query cache and no socket of its own. Every dependency arrives as a
 * port (`ChatPorts`): the REST endpoints, the hub, the media pipeline, the place the server's word
 * is kept, the clock. The app gives it the production adapters (`lib/chatAdapters.ts`); a test gives
 * it in-memory ones and drives the whole lifecycle with no browser and no backend.
 *
 * What it owns, and the rules a caller can rely on:
 *
 * - **A send.** `send(conversationId, intent)` draws the bubble at once and returns its `clientId`
 *   (the server's idempotency key). The intent carries what the trainer gave — a raw picture, a raw
 *   film, a raw recording — and the session prepares it (JPEG, the film's compression, AAC) ONCE:
 *   the prepared file is kept with the send, so a retry never prepares twice. A refusal while
 *   preparing (a film too long, a picture that cannot be read) removes the bubble and says why on
 *   the notice channel. A transport failure leaves the bubble standing as `failed`.
 * - **A retry** sends again under the same `clientId`; the server returns the stored message for a
 *   send that did land. `resynced` from the hub (the socket came back after a gap) re-reads the
 *   server's word and then retries every failed send, oldest first.
 * - **A film or a voice note** goes slot → PUT → confirm. A slot that answers with no upload target
 *   is a replay of a send that was already stored: its message lands and nothing is uploaded. The
 *   send can be stopped until the bytes are in; from the PUT's answer on it cannot.
 * - **The server's word** (`ServerTruth`) only ever receives server answers. A pending send leaves
 *   the moment its answer is put there; the caller merges what remains (`pendingFor`).
 * - **The read mark** moves forward only, only while the conversation is visible, and a refused mark
 *   is sent again by the next `seen`.
 * - **Typing** counts for three seconds and ends at once when the other side's message arrives.
 * - **Broadcasts** are put into the server's word, except the two recording cards, whose exercise is
 *   worded for the reader and so are re-read instead; the inbox is re-read on a 400 ms debounce.
 */

import type {
  TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse,
  TrainWithMeModulesChatDtosResponsesMessageResponse as MessageResponse,
  TrainWithMeModulesMediaDtosResponsesCreateVideoSlotResponse as SlotUpload,
} from '~/shared/api/generated/model';

import type { LocalSend, PendingSend } from './chatMessage';
import { wireWaveform } from './voicePeaks';

// ── What the trainer can send ───────────────────────────────────────────────────────────────

/** A card the trainer picked, with what its bubble shows until the server's snapshot arrives. */
export type CardChoice =
  | {
      readonly kind: 'exercise' | 'clipRequest';
      readonly exerciseId: number;
      readonly name: string;
      readonly imageUrl: string | null;
      readonly muscle: string | null;
    }
  | {
      readonly kind: 'plan';
      readonly planAssignmentId: number;
      readonly name: string;
      readonly category: string | null;
      readonly imageUrl: string | null;
    }
  | {
      readonly kind: 'summary';
      readonly workoutExecutionId: number;
      readonly title: string;
      readonly setsDone: number;
      readonly setsTotal: number;
    };

export type SendIntent =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'image'; readonly file: File; readonly caption: string }
  | { readonly kind: 'video'; readonly file: File; readonly caption: string }
  | {
      readonly kind: 'voice';
      readonly recording: Blob;
      readonly durationSeconds: number;
      /** The recorder's levels, 0..31 each, one per tick. */
      readonly levels: readonly number[];
    }
  | { readonly kind: 'card'; readonly choice: CardChoice };

/** A sentence the trainer has to hear, as an i18n key the caller words. */
export interface ChatNotice {
  readonly key: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

// ── The ports ───────────────────────────────────────────────────────────────────────────────

export interface SlotAnswer {
  readonly message: MessageResponse | null;
  /** `null` when the slot replayed a send the server already holds. */
  readonly upload: SlotUpload | null;
}

/** The chat's REST endpoints. Remote but owned: `httpChatApi` in the app, an in-memory server in tests. */
export interface ChatApi {
  postText(conversationId: number, clientId: string, content: string): Promise<MessageResponse>;
  postImage(conversationId: number, clientId: string, file: File, caption: string): Promise<MessageResponse>;
  postCard(conversationId: number, clientId: string, choice: CardChoice): Promise<MessageResponse>;
  videoSlot(
    conversationId: number,
    request: { clientId: string; sizeBytes: number; durationSeconds: number; caption: string },
  ): Promise<SlotAnswer>;
  voiceSlot(
    conversationId: number,
    request: { clientId: string; sizeBytes: number; durationSeconds: number; waveform: number[] | null },
  ): Promise<SlotAnswer>;
  confirmMedia(mediaId: number): Promise<void>;
  markRead(conversationId: number, lastMessageId: number): Promise<void>;
}

export interface HubEvents {
  message(message: MessageResponse): void;
  created(conversation: ConversationResponse): void;
  typing(conversationId: number, userId: string): void;
  status(status: RealtimeStatus): void;
  /** The socket came back after a gap: what was said meanwhile was never delivered. */
  resynced(): void;
}

/** The live connection. Remote but owned: the SignalR hub in the app, a scriptable hub in tests. */
export interface ChatHub {
  connect(events: HubEvents): () => void;
  join(conversationId: number): void;
  typing(conversationId: number): void;
}

/** Where the server's word is kept. The query cache in the app; a record of writes in tests. */
export interface ServerTruth {
  put(message: MessageResponse): void;
  reread(scope: 'inbox' | 'everything' | { readonly thread: number }): void;
  markedRead(conversationId: number): void;
}

export type Prepared<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly notice: ChatNotice };

/** The browser's media work. True external: workers, codecs and the bucket's PUT. */
export interface MediaPort {
  prepareImage(file: File): Promise<Prepared<File>>;
  prepareVideo(
    file: File,
    onProgress: (fraction: number) => void,
    signal: AbortSignal,
  ): Promise<Prepared<{ file: File; durationSeconds: number }>>;
  encodeVoice(recording: Blob): Promise<Prepared<File>>;
  put(upload: SlotUpload, file: File, onProgress: (fraction: number) => void, signal: AbortSignal): Promise<number>;
  /** An object URL for a picked file or a recording — the bubble's picture or sound until the server's. */
  preview(file: Blob): string;
  revoke(url: string): void;
}

export interface Clock {
  now(): number;
  /** Run `run` after `ms`; the returned function cancels it. */
  after(ms: number, run: () => void): () => void;
}

export interface ChatPorts {
  readonly api: ChatApi;
  readonly hub: ChatHub;
  readonly truth: ServerTruth;
  readonly media: MediaPort;
  readonly clock: Clock;
  readonly newClientId: () => string;
}

// ── The session ─────────────────────────────────────────────────────────────────────────────

export interface ChatSession {
  /** Connects the hub; the returned function disconnects it and drops every timer. */
  readonly start: () => () => void;
  /** For `useSyncExternalStore`: the listener runs after every change a caller could draw. */
  readonly subscribe: (listener: () => void) => () => void;
  /** Changes whenever anything below does; cheap to compare. */
  readonly version: () => number;
  readonly status: () => RealtimeStatus;
  readonly pendingFor: (conversationId: number) => PendingSend[];
  /** `clientId` → object URL of what this browser picked or recorded. */
  readonly previews: () => ReadonlyMap<string, string>;
  readonly isTyping: (conversationId: number) => boolean;
  readonly send: (conversationId: number, intent: SendIntent) => string;
  readonly retry: (clientId: string) => void;
  readonly remove: (clientId: string) => void;
  readonly seen: (conversationId: number, newestServerId: number | null, visible: boolean) => void;
  readonly typed: (conversationId: number) => void;
  readonly join: (conversationId: number) => void;
  readonly onNotice: (listener: (notice: ChatNotice) => void) => () => void;
}

export const TYPING_HOLD_MS = 3_000;
export const TYPING_THROTTLE_MS = 1_000;
export const INBOX_DEBOUNCE_MS = 400;

interface Entry {
  readonly conversationId: number;
  readonly intent: SendIntent;
  readonly order: number;
  wire: MessageResponse;
  local: LocalSend;
  /** What preparing the intent produced, kept so a retry never prepares twice. */
  prepared: File | { file: File; durationSeconds: number } | null;
  stop: AbortController | null;
}

/** Preparing refused the file: the send goes, and the trainer is told why. */
class Refused extends Error {
  readonly notice: ChatNotice;

  constructor(notice: ChatNotice) {
    super('The file was refused while it was prepared');
    this.notice = notice;
  }
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function optimisticWire(intent: SendIntent): Omit<MessageResponse, 'clientId' | 'conversationId' | 'createdAt' | 'senderUserId'> {
  switch (intent.kind) {
    case 'text':
      return { contentType: 'Text', content: intent.text.trim() };
    case 'image':
      return {
        contentType: 'Image',
        content: intent.caption.trim() === '' ? null : intent.caption.trim(),
        media: { url: null, mediaId: 0 },
      };
    case 'video':
      return {
        contentType: 'Video',
        content: intent.caption.trim() === '' ? null : intent.caption.trim(),
        video: { status: 'Processing', mediaId: 0 },
      };
    case 'voice':
      return {
        contentType: 'Voice',
        voice: {
          status: 'Processing',
          mediaId: 0,
          durationSeconds: Math.max(1, Math.round(intent.durationSeconds)),
          waveform: wireWaveform(intent.levels),
        },
      };
    case 'card':
      return cardWire(intent.choice);
  }
}

function cardWire(choice: CardChoice): Omit<MessageResponse, 'clientId' | 'conversationId' | 'createdAt' | 'senderUserId'> {
  switch (choice.kind) {
    case 'exercise':
      return {
        contentType: 'ExerciseCard',
        content: choice.name,
        exerciseCard: {
          exerciseId: choice.exerciseId,
          name: choice.name,
          exerciseType: null,
          primaryMuscleGroup: choice.muscle,
          coverImageUrl: choice.imageUrl,
        },
      };
    case 'clipRequest':
      return {
        contentType: 'ExerciseClipRequest',
        content: choice.name,
        exerciseClipRequest: {
          exercise: {
            exerciseId: choice.exerciseId,
            name: choice.name,
            exerciseType: null,
            primaryMuscleGroup: choice.muscle,
            coverImageUrl: choice.imageUrl,
          },
          exerciseAvailable: true,
        },
      };
    case 'plan':
      return {
        contentType: 'TrainingPlan',
        content: choice.name,
        trainingPlan: {
          planAssignmentId: choice.planAssignmentId,
          name: choice.name,
          category: choice.category,
          schedulingMode: null,
          coverImageUrl: choice.imageUrl,
        },
      };
    case 'summary':
      return {
        contentType: 'WorkoutSummary',
        content: choice.title,
        workoutSummary: {
          workoutExecutionId: choice.workoutExecutionId,
          title: choice.title,
          setsDone: choice.setsDone,
          setsTotal: choice.setsTotal,
          highlights: [],
        },
      };
  }
}

function firstStage(intent: SendIntent): LocalSend['stage'] {
  if (intent.kind === 'video') return 'compressing';
  if (intent.kind === 'image' || intent.kind === 'voice') return 'sending';
  return null;
}

export function createChatSession(ports: ChatPorts, me: { readonly userId: string }): ChatSession {
  const { api, hub, truth, media, clock } = ports;
  const entries = new Map<string, Entry>();
  const previews = new Map<string, string>();
  const typingUntil = new Map<number, number>();
  const typingTimers = new Map<number, () => void>();
  const marked = new Map<number, number>();
  const lastTyped = new Map<number, number>();
  const listeners = new Set<() => void>();
  const noticeListeners = new Set<(notice: ChatNotice) => void>();
  let currentStatus: RealtimeStatus = 'idle';
  let version = 0;
  let order = 0;
  let cancelInbox: (() => void) | null = null;

  const changed = () => {
    version += 1;
    for (const listener of listeners) listener();
  };

  const notice = (value: ChatNotice) => {
    for (const listener of noticeListeners) listener(value);
  };

  const rereadInbox = () => {
    cancelInbox?.();
    cancelInbox = clock.after(INBOX_DEBOUNCE_MS, () => {
      cancelInbox = null;
      truth.reread('inbox');
    });
  };

  const patch = (clientId: string, local: Partial<LocalSend>) => {
    const entry = entries.get(clientId);
    if (entry === undefined) return;
    entry.local = { ...entry.local, ...local };
    changed();
  };

  const drop = (clientId: string, revoke: boolean) => {
    const entry = entries.get(clientId);
    entry?.stop?.abort();
    entries.delete(clientId);
    if (revoke) {
      const url = previews.get(clientId);
      if (url !== undefined) media.revoke(url);
      previews.delete(clientId);
    }
    changed();
  };

  const land = (clientId: string, message: MessageResponse | null) => {
    if (message !== null) truth.put(message);
    // The picture this browser picked stays with the server's row: a film still processing has none.
    entries.delete(clientId);
    changed();
    rereadInbox();
  };

  const throughSlot = async (
    clientId: string,
    entry: Entry,
    file: File,
    slot: () => Promise<SlotAnswer>,
  ) => {
    const stop = new AbortController();
    entry.stop = stop;
    const answer = await slot();
    if (answer.upload === null) {
      land(clientId, answer.message);
      return;
    }
    patch(clientId, { stage: 'sending', progress: 0 });
    const mediaId = await media.put(answer.upload, file, (fraction) => {
      patch(clientId, { progress: fraction });
    }, stop.signal);
    // The bytes are in the bucket: from here on nothing stops the server acting on them.
    entry.stop = null;
    patch(clientId, { stage: 'processing', progress: null });
    await api.confirmMedia(mediaId);
    land(clientId, answer.message);
  };

  const run = async (clientId: string) => {
    const entry = entries.get(clientId);
    if (entry === undefined) return;
    const id = entry.conversationId;
    const intent = entry.intent;
    switch (intent.kind) {
      case 'text':
        land(clientId, await api.postText(id, clientId, intent.text.trim()));
        return;
      case 'card':
        land(clientId, await api.postCard(id, clientId, intent.choice));
        return;
      case 'image': {
        if (entry.prepared === null) {
          const prepared = await media.prepareImage(intent.file);
          if (!prepared.ok) throw new Refused(prepared.notice);
          entry.prepared = prepared.value;
        }
        const file = entry.prepared as File;
        land(clientId, await api.postImage(id, clientId, file, intent.caption.trim()));
        return;
      }
      case 'video': {
        if (entry.prepared === null) {
          const stop = new AbortController();
          entry.stop = stop;
          patch(clientId, { stage: 'compressing', progress: 0 });
          const prepared = await media.prepareVideo(intent.file, (fraction) => {
            patch(clientId, { progress: fraction });
          }, stop.signal);
          if (!prepared.ok) throw new Refused(prepared.notice);
          entry.prepared = prepared.value;
        }
        const film = entry.prepared as { file: File; durationSeconds: number };
        await throughSlot(clientId, entry, film.file, () =>
          api.videoSlot(id, {
            clientId,
            sizeBytes: film.file.size,
            durationSeconds: Math.max(1, Math.round(film.durationSeconds)),
            caption: intent.caption.trim(),
          }),
        );
        return;
      }
      case 'voice': {
        if (entry.prepared === null) {
          const prepared = await media.encodeVoice(intent.recording);
          if (!prepared.ok) throw new Refused(prepared.notice);
          entry.prepared = prepared.value;
        }
        const file = entry.prepared as File;
        await throughSlot(clientId, entry, file, () =>
          api.voiceSlot(id, {
            clientId,
            sizeBytes: file.size,
            durationSeconds: Math.max(1, Math.round(intent.durationSeconds)),
            waveform: wireWaveform(intent.levels),
          }),
        );
        return;
      }
    }
  };

  const attempt = (clientId: string) => {
    const entry = entries.get(clientId);
    if (entry === undefined) return;
    entry.local = { status: 'sending', stage: firstStage(entry.intent), progress: null };
    changed();
    run(clientId).catch((error: unknown) => {
      if (!entries.has(clientId)) return;
      if (error instanceof Refused) {
        drop(clientId, true);
        notice(error.notice);
        return;
      }
      if (isAbort(error)) {
        drop(clientId, true);
        return;
      }
      const failed = entries.get(clientId);
      if (failed === undefined) return;
      failed.stop = null;
      patch(clientId, { status: 'failed', stage: null, progress: null });
    });
  };

  const markTyping = (conversationId: number) => {
    typingUntil.set(conversationId, clock.now() + TYPING_HOLD_MS);
    typingTimers.get(conversationId)?.();
    typingTimers.set(
      conversationId,
      clock.after(TYPING_HOLD_MS, () => {
        typingUntil.delete(conversationId);
        typingTimers.delete(conversationId);
        changed();
      }),
    );
    changed();
  };

  const clearTyping = (conversationId: number) => {
    if (!typingUntil.has(conversationId)) return;
    typingUntil.delete(conversationId);
    typingTimers.get(conversationId)?.();
    typingTimers.delete(conversationId);
    changed();
  };

  return {
    start() {
      const disconnect = hub.connect({
        message(message) {
          const conversationId = message.conversationId;
          if (typeof conversationId === 'number') {
            if (message.contentType === 'ExerciseClip' || message.contentType === 'ExerciseClipRequest') {
              truth.reread({ thread: conversationId });
            } else {
              truth.put(message);
            }
            if (message.senderUserId !== me.userId) clearTyping(conversationId);
          }
          rereadInbox();
        },
        created(conversation) {
          if (typeof conversation.id === 'number') hub.join(conversation.id);
          rereadInbox();
        },
        typing(conversationId, userId) {
          if (userId !== me.userId) markTyping(conversationId);
        },
        status(next) {
          if (next === currentStatus) return;
          currentStatus = next;
          changed();
        },
        resynced() {
          truth.reread('everything');
          const failed = [...entries.entries()]
            .filter(([, entry]) => entry.local.status === 'failed')
            .sort(([, a], [, b]) => a.order - b.order);
          for (const [clientId] of failed) attempt(clientId);
        },
      });
      return () => {
        disconnect();
        cancelInbox?.();
        cancelInbox = null;
        for (const cancel of typingTimers.values()) cancel();
        typingTimers.clear();
        typingUntil.clear();
        currentStatus = 'idle';
        changed();
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    version: () => version,
    status: () => currentStatus,
    pendingFor(conversationId) {
      const out: PendingSend[] = [];
      for (const entry of entries.values()) {
        if (entry.conversationId === conversationId) out.push({ wire: entry.wire, local: entry.local });
      }
      return out;
    },
    previews: () => previews,
    isTyping: (conversationId) => (typingUntil.get(conversationId) ?? 0) > clock.now(),
    send(conversationId, intent) {
      const clientId = ports.newClientId();
      entries.set(clientId, {
        conversationId,
        intent,
        order: ++order,
        wire: {
          ...optimisticWire(intent),
          clientId,
          conversationId,
          createdAt: new Date(clock.now()).toISOString(),
          senderUserId: me.userId,
        },
        local: { status: 'sending', stage: firstStage(intent), progress: null },
        prepared: null,
        stop: null,
      });
      if (intent.kind === 'image' || intent.kind === 'video') previews.set(clientId, media.preview(intent.file));
      if (intent.kind === 'voice') previews.set(clientId, media.preview(intent.recording));
      attempt(clientId);
      return clientId;
    },
    retry(clientId) {
      if (entries.get(clientId)?.local.status !== 'failed') return;
      attempt(clientId);
    },
    remove(clientId) {
      drop(clientId, true);
    },
    seen(conversationId, newestServerId, visible) {
      if (!visible || newestServerId === null) return;
      if ((marked.get(conversationId) ?? -1) >= newestServerId) return;
      marked.set(conversationId, newestServerId);
      api
        .markRead(conversationId, newestServerId)
        .then(() => {
          truth.markedRead(conversationId);
        })
        .catch(() => {
          if (marked.get(conversationId) === newestServerId) marked.delete(conversationId);
        });
    },
    typed(conversationId) {
      const now = clock.now();
      if (now - (lastTyped.get(conversationId) ?? -Infinity) < TYPING_THROTTLE_MS) return;
      lastTyped.set(conversationId, now);
      hub.typing(conversationId);
    },
    join(conversationId) {
      hub.join(conversationId);
    },
    onNotice(listener) {
      noticeListeners.add(listener);
      return () => {
        noticeListeners.delete(listener);
      };
    },
  };
}

